/* ============================================================
   Steel & Shine - Core Interactive Engine
   High Performance Luxury E-Commerce Engine
   ============================================================ */

'use strict';

/* ==========================================================================
   APPLE FLUID PHYSICS & ZERO-LATENCY INTERACTION ENGINE
   Direct implementation of Apple WWDC & Emil Kowalski / Linear Standards
   ========================================================================== */

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* --- 1. Feedback Instantâneo de Toque a 0ms (Kill Latency) --- */
(function initTactileTouchEngine() {
    if (prefersReducedMotion) return;
    const TACTILE_SELECTOR = 'button, a.btn, .btn, .btn-primary, .btn-ghost, .btn-gold, .btn-luxury, .hero-btn-primary, .hero-btn-secondary, .pdp-cta-btn, #addToCartBtn, .ss-featured-viewall, .ss-cat-3d-nav, .js-quick-add, .product-card, .featured-product-card, .filter-tab, .ss-size-nav-btn, .lang-btn, .lang-current, .nav-icon, .cart-trigger, .cart-icon, .ss-wishlist-header, .search-trigger-btn, .ss-btn-wa, .ss-shipping-btn-wa, .ss-care-btn-wa, .ss-faq-btn-whatsapp, .ss-wishlist-btn, .ss-stock-submit, .thumbnail-btn, [data-tactile]';

    window.addEventListener('pointerdown', function(e) {
        // Ignora cliques com botões secundários
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        const target = e.target.closest(TACTILE_SELECTOR);
        if (!target || target.disabled) return;

        target.classList.add('is-pressed');

        const cleanUp = function() {
            target.classList.remove('is-pressed');
            window.removeEventListener('pointerup', cleanUp);
            window.removeEventListener('pointercancel', cleanUp);
            window.removeEventListener('pointerleave', cleanUp);
        };

        window.addEventListener('pointerup', cleanUp, { passive: true, once: true });
        window.addEventListener('pointercancel', cleanUp, { passive: true, once: true });
        window.addEventListener('pointerleave', cleanUp, { passive: true, once: true });
    }, { capture: true, passive: true });
})();

/* --- 2. Apple Mathematical Physics & Inertial Solvers --- */
window.ApplePhysics = {
    /**
     * Projeção Exponencial de Desaceleração da Apple (UIScrollView Momentum Formula)
     * projected = currentPosition + (velocity / 1000) * (0.998 / (1 - 0.998))
     */
    projectMomentum: function(currentPos, velocity, decelerationRate = 0.998) {
        const factor = (decelerationRate / (1 - decelerationRate)) / 1000;
        return currentPos + (velocity * factor);
    },

    /**
     * Resistência Progressiva Elástica (Apple Rubber-Banding)
     * dx = (1.0 - (1.0 / ((offset * constant / dimension) + 1.0))) * dimension
     */
    rubberBand: function(offset, dimension, constant = 0.55) {
        return (1.0 - (1.0 / ((offset * constant / dimension) + 1.0))) * dimension;
    },

    /**
     * Interrompibilidade: Extrai o valor real renderizado no ecrã (Presentation Value)
     */
    getPresentationValue: function(element) {
        if (!element) return { x: 0, y: 0 };
        const style = window.getComputedStyle(element);
        const transform = style.transform || style.webkitTransform;
        if (!transform || transform === 'none') return { x: 0, y: 0 };
        try {
            const matrix = new DOMMatrix(transform);
            return { x: matrix.m41, y: matrix.m42 };
        } catch (err) {
            return { x: 0, y: 0 };
        }
    }
};

/* --- 3. Robust Mobile Drawer Controller --- */
function initInterruptibleDrawer() {
    const drawer = document.getElementById('mobileMenu');
    const hamburger = document.getElementById('menuToggle');
    const closeBtn = document.getElementById('mobileMenuClose');
    if (!drawer) return;

    function openDrawer(e) {
        if (e) {
            if (e.cancelable) e.preventDefault();
            e.stopPropagation();
        }
        drawer.style.transform = '';
        drawer.style.transition = '';
        drawer.classList.add('open');
        drawer.setAttribute('aria-hidden', 'false');
        if (hamburger) hamburger.classList.add('open');
        document.body.style.overflow = 'hidden';
    }

    function closeDrawer(e) {
        if (e) {
            if (e.cancelable && (!e.target || !e.target.closest('a:not(.mobile-menu-close)'))) {
                e.preventDefault();
            }
            e.stopPropagation();
        }
        drawer.style.transform = '';
        drawer.style.transition = '';
        drawer.classList.remove('open');
        drawer.setAttribute('aria-hidden', 'true');
        if (hamburger) hamburger.classList.remove('open');
        document.body.style.overflow = '';
    }

    if (hamburger) {
        hamburger.onclick = function(e) {
            if (drawer.classList.contains('open')) {
                closeDrawer(e);
            } else {
                openDrawer(e);
            }
        };
    }

    if (closeBtn) {
        closeBtn.onclick = function(e) {
            closeDrawer(e);
        };
        closeBtn.addEventListener('touchend', function(e) {
            closeDrawer(e);
        }, { passive: false });
    }

    drawer.querySelectorAll('a[href^="#"]').forEach(function(link) {
        link.addEventListener('click', function() {
            closeDrawer();
        });
    });

    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && drawer.classList.contains('open')) {
            closeDrawer();
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInterruptibleDrawer);
} else {
    initInterruptibleDrawer();
}


/* --- Announcement bar dynamic height & scroll management --- */
function setAnnouncementHeight() {
    const ab = document.getElementById('announcementBar');
    if (ab) {
        document.documentElement.style.setProperty('--announcement-height', ab.offsetHeight + 'px');
    }
}
window.addEventListener('load', setAnnouncementHeight);
window.addEventListener('resize', setAnnouncementHeight);

/* Hide announcement bar on scroll down, show on scroll up */
let lastScrollY = 0;
window.addEventListener('scroll', () => {
    const navbar = document.getElementById('navbar');
    const announcementBar = document.getElementById('announcementBar');
    if (!navbar) return;
    const currentScrollY = window.scrollY;

    if (currentScrollY > 60) {
        navbar.classList.add('scrolled');
        if (currentScrollY > lastScrollY && currentScrollY > 120) {
            if (announcementBar) announcementBar.classList.add('hidden-up');
            navbar.classList.add('announcement-hidden');
        } else {
            if (announcementBar) announcementBar.classList.remove('hidden-up');
            navbar.classList.remove('announcement-hidden');
        }
    } else {
        navbar.classList.remove('scrolled');
        if (announcementBar) announcementBar.classList.remove('hidden-up');
        navbar.classList.remove('announcement-hidden');
    }
    lastScrollY = currentScrollY;
}, { passive: true });

/* ============================================================
   DOM Interactive Behaviors
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {

    /* ------ Navbar Scroll Class ------ */
    const navbar = document.getElementById('navbar');
    if (navbar) {
        window.addEventListener('scroll', () => {
            if (window.scrollY > 60) {
                navbar.classList.add('scrolled');
            } else {
                navbar.classList.remove('scrolled');
            }
        }, { passive: true });
    }

    /* ------ FAQ Accordion ------ */
    document.querySelectorAll('.faq-question').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const item = btn.closest('.faq-item');
            if (!item) return;
            const isOpen = item.classList.contains('open');
            const parentCol = item.closest('.faq-column') || item.closest('.faq-two-columns') || item.closest('#faq');
            if (parentCol) {
                parentCol.querySelectorAll('.faq-item.open').forEach(i => {
                    if (i !== item) i.classList.remove('open');
                });
            } else {
                document.querySelectorAll('.faq-item.open').forEach(i => {
                    if (i !== item) i.classList.remove('open');
                });
            }
            if (isOpen) {
                item.classList.remove('open');
            } else {
                item.classList.add('open');
            }
        });
    });

    /* ------ Product Filter Tabs ------ */
    document.querySelectorAll('.filter-tab').forEach(tab => {
        tab.addEventListener('click', () => {
            document.querySelectorAll('.filter-tab').forEach(t => t.classList.remove('active'));
            tab.classList.add('active');
            const filter = tab.dataset.filter;
            document.querySelectorAll('.product-card').forEach(card => {
                if (filter === 'all' || card.dataset.category === filter) {
                    card.classList.remove('hidden');
                } else {
                    card.classList.add('hidden');
                }
            });
        });
    });

    /* ------ Scroll Reveal (IntersectionObserver) ------ */
    if ('IntersectionObserver' in window) {
        const revealObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                    revealObserver.unobserve(entry.target);
                }
            });
        }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

        document.querySelectorAll('.trust-bar, .featured, .brand-story, .categories, .why-steel, .testimonials, .email-capture, .faq').forEach(el => {
            el.classList.add('reveal');
            revealObserver.observe(el);
        });

        document.querySelectorAll('.product-card, .feature-card, .testimonial-card, .category-card').forEach((el, i) => {
            el.style.transitionDelay = `${i * 0.07}s`;
            el.classList.add('reveal');
            revealObserver.observe(el);
        });
    }

    /* ------ Smooth Anchor Scrolling ------ */
    document.querySelectorAll('a[href^="#"]').forEach(a => {
        a.addEventListener('click', e => {
            const href = a.getAttribute('href');
            if (href === '#') return;
            const target = document.querySelector(href);
            if (target) {
                e.preventDefault();
                const offset = 80;
                const top = target.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({ top, behavior: 'smooth' });
            }
        });
    });

    /* ============================================================
       SS Hover Video Controller (Unified & Leak-Free)
       Strategy:
         1. Lazy preload metadata when within 400px of viewport.
         2. On mouseenter: play with catch fallback.
         3. On mouseleave: pause + reset currentTime.
       ============================================================ */
    (function() {
        var hoverVideos = document.querySelectorAll('[data-hover-video]');
        if (!hoverVideos.length) return;

        if ('IntersectionObserver' in window) {
            var preloadObs = new IntersectionObserver(function(entries) {
                entries.forEach(function(entry) {
                    if (entry.isIntersecting) {
                        var v = entry.target;
                        if (v.getAttribute('preload') === 'none') {
                            v.preload = 'metadata';
                        }
                        preloadObs.unobserve(v);
                    }
                });
            }, { rootMargin: '400px 0px' });

            hoverVideos.forEach(function(v) {
                preloadObs.observe(v);
            });
        }

        hoverVideos.forEach(function(video) {
            if (video._hoverInit) return;
            video._hoverInit = true;
            var card = video.closest('.group') || video.closest('.featured-product-card');
            if (!card) return;

            card.addEventListener('mouseenter', function() {
                if (video.readyState < 2) {
                    video.preload = 'auto';
                    video.load();
                    var playOnLoad = function() {
                        video.play().catch(function() {});
                        video.removeEventListener('loadeddata', playOnLoad);
                    };
                    video.addEventListener('loadeddata', playOnLoad);
                } else {
                    video.play().catch(function() {});
                }
            });

            card.addEventListener('mouseleave', function() {
                video.pause();
                setTimeout(function() {
                    try { video.currentTime = 0; } catch (e) {}
                }, 150);
            });
        });
    })();

    /* ------ Quick Add to Cart Handler (Shopify AJAX API) ------ */
    document.querySelectorAll('.js-quick-add').forEach(function(btn) {
        btn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            var variantId = this.dataset.variantId;
            if (!variantId) return;
            var originalText = this.innerHTML;
            this.innerHTML = '...';
            this.disabled = true;
            var self = this;

            var lang = (document.documentElement.lang || 'nl').toLowerCase().slice(0, 2);
            var props = {
                'Garantie': '2 Jaar Officiële Garantie (316L Staal & 18K Goud PVD)',
                'Verpakking': 'Signature Giftbox Inbegrepen (Klaar om te Geven)'
            };
            if (lang === 'pt') {
                props = {
                    'Garantia': 'Garantia Oficial 2 Anos (Aço 316L & Ouro 18K PVD)',
                    'Embalagem': 'Caixa de Oferta Signature Incluída (Pronta a Oferecer)'
                };
            } else if (lang === 'en') {
                props = {
                    'Warranty': '2-Year Official Warranty (316L Steel & 18K Gold PVD)',
                    'Packaging': 'Signature Gift Box Included (Ready to Gift)'
                };
            } else if (lang === 'de') {
                props = {
                    'Garantie': '2 Jahre Offizielle Garantie (316L Stahl & 18K Gold PVD)',
                    'Verpackung': 'Signature Geschenkbox Inbegriffen (Bereit zum Verschenken)'
                };
            } else if (lang === 'fr') {
                props = {
                    'Garantie': 'Garantie Officielle 2 Ans (Acier 316L & Or 18K PVD)',
                    'Coffret': 'Coffret Signature Inclus (Prêt à Offrir)'
                };
            }

            fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: Number(variantId),
                    quantity: 1,
                    properties: props
                })
            })
            .then(function(res) {
                if (!res.ok) throw new Error('Add failed');
                return res.json();
            })
            .then(function() {
                self.innerHTML = '✓';
                return fetch('/cart.js');
            })
            .then(function(r) { return r.json(); })
            .then(function(cart) {
                document.querySelectorAll('.cart-badge').forEach(function(badge) {
                    badge.textContent = cart.item_count || '';
                });
                if (window.openCartDrawer) {
                    window.refreshCartDrawer();
                    window.openCartDrawer();
                }
                setTimeout(function() {
                    self.innerHTML = originalText;
                    self.disabled = false;
                }, 2000);
            })
            .catch(function() {
                self.innerHTML = originalText;
                self.disabled = false;
            });
        });
    });
});
